import { SearchOutlined } from '@ant-design/icons';
import { ConfigProvider, Layout, Flex, Skeleton, Row, Col, Select, DatePicker, Button, Table, notification, TableProps, Card, Modal } from 'antd';
import { SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LoaderSpinner } from '../../components/common/Loader';
import { getReportFilterList, getReportData, exportReportData } from '../../service/httpsCalls';
import https from '../../service/httpsService';
import { createTableConfig } from "../Billing/tableConfig";
import BusPassReportForm from './components/BusPassReportForm';
import UpdateAppointmentTime from './components/UpdateAppointmentTime';
const { RangePicker } = DatePicker;

/**
 * Reports Route Component
 * Displays reports page content.
 */

interface PaginationObject {
    totalRecords: number,
    loading: boolean,
    pageSize: number,
    currentPage: number,
    sortInfo: SorterResult<any>
}

interface ReportType {
    reportConfig : string,
    reportId : number,
    reportName : string
}

type DropdownValue = string | number | null | Array<string | number>;

interface DropdownOption {
    value: string | number;
    label: string;
}

interface DropdownApiConfig {
    url: string;
    method?: string;
    valueField?: string;
    labelField?: string;
}

interface DropdownConfig {
    label: string;
    key: string;
    param?: string;
    mode?: 'single' | 'multiple';
    data?: DropdownOption[];
    dataApi?: DropdownApiConfig;
    default?: DropdownValue;
}

interface DynamicFilterPayload {
    key: string;
    label: string;
    param: string;
    mode?: 'single' | 'multiple';
    value: DropdownValue;
}

type NotificationType = 'success' | 'info' | 'warning' | 'error';

const REPORT_TABLE_CONTAINER_ID = "reports-table-container";

const Reports: React.FC = () => {
    const [api, contextHolder] = notification.useNotification();
    const [filterLoader, setFilterLoader] = useState(false);
    const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
    const [selectedReportType, setSelectedReportType] = useState<string>(null);
    const [tableKey, setTableKey] = useState(0); // Key state to refresh table
    const [menuVisible, setMenuVisible] = useState(false);
    const [primaryKey, setPrimaryKey] = useState<string | null>(null);
    const [reportConfig, setReportConfig] = useState<string>('');
    const [columns, setColumns] = useState<any>([]);
    const [reportData, setReportData] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [tableHeight, setTableHeight] = useState(300);
    const [filterConfig, setFilterConfig] = useState<any>();
    const [dropdownValues, setDropdownValues] = useState<Record<string, DropdownValue>>({});
    const [dropdownOptions, setDropdownOptions] = useState<Record<string, DropdownOption[]>>({});
    const [dropdownLoading, setDropdownLoading] = useState<Record<string, boolean>>({});
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [calendarRange, setCalendarRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    let paginationObject: PaginationObject = {
        totalRecords: 0,
        loading: false,
        pageSize: 50,
        currentPage: 1,
        sortInfo: {}
    }
    const [tablePagination, setTablePagination] = useState(paginationObject);
    const filtersRef = React.useRef<{
        selectedReportType: string | null;
        startDate: Dayjs | null;
        endDate: Dayjs | null;
    }>({
        selectedReportType: null,
        startDate: null,
        endDate: null,
    });
    const [exportLoader, setExportLoader] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [busPassModalOpen, setBusPassModalOpen] = useState(false);
    const [busPassFormValues, setBusPassFormValues] = useState<any>({});
    const [updateApptModalOpen, setUpdateApptModalOpen] = useState(false);
    const [updateApptValues, setUpdateApptValues] = useState<any>({});
    const resetTableScrollPosition = useCallback(() => {
        window.requestAnimationFrame(() => {
            const container = document.getElementById(REPORT_TABLE_CONTAINER_ID);
            const scrollElements = container?.querySelectorAll<HTMLElement>(
                ".ant-table-body, .ant-table-header, .ant-table-content"
            );

            scrollElements?.forEach((element) => {
                element.scrollLeft = 0;
            });
        });
    }, []);

    const openNotificationWithIcon = (type: NotificationType, message: string, description: string) => {
        api[type]({
            message: message,
            description: description
        });
    };
    const dropdownConfigs = useMemo<DropdownConfig[]>(() => {
        if (!filterConfig?.Dropdown) {
            return [];
        }
        return Array.isArray(filterConfig.Dropdown) ? filterConfig.Dropdown : [];
    }, [filterConfig]);

    const mapDropdownOptions = useCallback((entries: any[], config?: DropdownConfig): DropdownOption[] => {
        if (!Array.isArray(entries)) {
            return [];
        }

        return entries.map((entry) => {
            if (entry === null || entry === undefined) {
                return null;
            }

            if (typeof entry !== 'object') {
                return {
                    value: entry,
                    label: String(entry)
                } as DropdownOption;
            }

            const valueKey = config?.dataApi?.valueField;
            const labelKey = config?.dataApi?.labelField;
            const resolvedValueRaw = valueKey ? entry[valueKey] : (entry.value ?? entry.id ?? entry.key ?? entry.code ?? null);
            const resolvedLabelRaw = labelKey ? entry[labelKey] : (entry.label ?? entry.name ?? entry.description ?? entry.title ?? '');

            if (resolvedValueRaw === undefined || resolvedValueRaw === null) {
                return null;
            }
            const resolvedValue = typeof resolvedValueRaw === 'number' || typeof resolvedValueRaw === 'string'
                ? resolvedValueRaw
                : String(resolvedValueRaw);
            const resolvedLabel = resolvedLabelRaw !== undefined && resolvedLabelRaw !== null && resolvedLabelRaw !== ''
                ? resolvedLabelRaw
                : String(resolvedValue);

            return {
                value: resolvedValue,
                label: typeof resolvedLabel === 'string' ? resolvedLabel : String(resolvedLabel)
            } as DropdownOption;
        }).filter((option): option is DropdownOption => !!option);
    }, []);

    const getDefaultDropdownValue = useCallback((config: DropdownConfig): DropdownValue => {
        if (config.mode === 'multiple') {
            if (Array.isArray(config.default)) {
                return config.default as Array<string | number>;
            }
            if (config.default === undefined || config.default === null) {
                return [];
            }
            return [config.default as string | number];
        }
        return config.default ?? null;
    }, []);

    useEffect(() => {
        let isActive = true;

        if (!dropdownConfigs.length) {
            setDropdownOptions({});
            setDropdownValues({});
            setDropdownLoading({});
            return () => {
                isActive = false;
            };
        }

        const defaults = dropdownConfigs.reduce((acc, config) => {
            acc[config.key] = getDefaultDropdownValue(config);
            return acc;
        }, {} as Record<string, DropdownValue>);

        setDropdownValues(defaults);
        setDropdownOptions({});
        setDropdownLoading({});

        dropdownConfigs.forEach((config) => {
            if (Array.isArray(config.data) && config.data.length) {
                setDropdownOptions((prev) => ({
                    ...prev,
                    [config.key]: mapDropdownOptions(config.data, config)
                }));
                return;
            }

            if (config.dataApi?.url) {
                setDropdownLoading((prev) => ({ ...prev, [config.key]: true }));
                const method = (config.dataApi.method ?? 'GET').toUpperCase();
                const fetcher = method === 'POST' ? https.post : https.get;
                const apiUrl = /^https?:\/\//i.test(config.dataApi.url)
                    ? config.dataApi.url
                    : (config.dataApi.url.startsWith('/') ? config.dataApi.url : `/${config.dataApi.url}`);

                fetcher(apiUrl)
                    .then((response) => {
                        if (!isActive) {
                            return;
                        }
                        const payload = Array.isArray(response?.data)
                            ? response.data
                            : (Array.isArray(response?.data?.items)
                                ? response.data.items
                                : (Array.isArray(response?.data?.data) ? response.data.data : []));

                        setDropdownOptions((prev) => ({
                            ...prev,
                            [config.key]: mapDropdownOptions(payload, config)
                        }));
                    })
                    .catch((error) => {
                        console.error(`Failed to load options for ${config.key}`, error);
                        if (!isActive) {
                            return;
                        }
                        setDropdownOptions((prev) => ({
                            ...prev,
                            [config.key]: []
                        }));
                    })
                    .finally(() => {
                        if (!isActive) {
                            return;
                        }
                        setDropdownLoading((prev) => ({ ...prev, [config.key]: false }));
                    });
            } else {
                setDropdownOptions((prev) => ({
                    ...prev,
                    [config.key]: []
                }));
            }
        });

        return () => {
            isActive = false;
        };
    }, [dropdownConfigs, getDefaultDropdownValue, mapDropdownOptions]);

    const openBusPassModal = (record: any) => {
        setBusPassFormValues(record);
        setBusPassModalOpen(true);
    };

    const openUpdateApptModal = (record: any) => {
        setUpdateApptValues(record);
        setUpdateApptModalOpen(true);
    };

    const handleBusPassModalClose = useCallback(() => {
        setBusPassModalOpen(false);
        setBusPassFormValues({});
    }, []);

    const handleUpdateApptModalClose = useCallback(() => {
        setUpdateApptModalOpen(false);
        setUpdateApptValues({});
    }, []);

    const handleBusPassReportGenerated = useCallback(() => {
        handleBusPassModalClose();
        search();
    }, [handleBusPassModalClose, search]);

    const handleAppointmentUpdated = useCallback(() => {
        handleUpdateApptModalClose();
        search();
    }, [handleUpdateApptModalClose, search]);

    const handleDynamicComponentAction = useCallback((componentName: string, record: any) => {
        if (componentName === 'BusPassReportForm') {
            openBusPassModal(record);
        }
        if (componentName === 'UpdateAppointmentTime') {
            openUpdateApptModal(record);
        }
    }, [openBusPassModal, openUpdateApptModal]);

    const dateFilters = useMemo(() => {
        if (!filterConfig?.DateFilters) {
            return undefined;
        }
        const rawFilters = filterConfig.DateFilters;
        return {
            isEnabled: rawFilters.isEnabled ?? rawFilters.enable ?? false,
            hasRestriction: rawFilters.hasRestriction ?? rawFilters.dateRestriction ?? false,
            pastDaysLimit: rawFilters.pastDaysLimit ?? rawFilters.min ?? null,
            futureDaysLimit: rawFilters.futureDaysLimit ?? rawFilters.max ?? null,
            range: typeof rawFilters.range === "number"
                ? rawFilters.range
                : (typeof rawFilters.rangeLimit === "number" ? rawFilters.rangeLimit : 0)
        };
    }, [filterConfig]);

    const isDateFilterEnabled = !!dateFilters?.isEnabled;

    const minSelectableDate = useMemo(() => {
        if (!dateFilters?.hasRestriction) {
            return undefined;
        }
        const today = dayjs().startOf('day');
        if (typeof dateFilters.pastDaysLimit === 'number' && dateFilters.pastDaysLimit >= 0) {
            return today.subtract(dateFilters.pastDaysLimit, 'day');
        }
        return today;
    }, [dateFilters]);

    const maxSelectableDate = useMemo(() => {
        if (!dateFilters?.hasRestriction) {
            return undefined;
        }
        const today = dayjs().startOf('day');
        if (typeof dateFilters.futureDaysLimit === 'number' && dateFilters.futureDaysLimit >= 0) {
            return today.add(dateFilters.futureDaysLimit, 'day');
        }
        return today;
    }, [dateFilters]);

    const normalizeRange = useCallback((rangeStart: Dayjs | null, rangeEnd: Dayjs | null): [Dayjs | null, Dayjs | null] => {
        if (rangeStart && rangeEnd && rangeStart.isAfter(rangeEnd, 'day')) {
            return [rangeEnd, rangeStart];
        }
        return [rangeStart, rangeEnd];
    }, []);

    const handleDropdownValueChange = useCallback((key: string, value: DropdownValue) => {
        setDropdownValues((prev) => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const buildDynamicFilterPayload = useCallback((): Record<string, DropdownValue> => {
        if (!dropdownConfigs.length) {
            return {};
        }

        return dropdownConfigs.reduce((acc, config) => {
            const selectedValue = dropdownValues?.[config.key];

            if (config.mode === 'multiple') {
                if (Array.isArray(selectedValue) && selectedValue.length) {
                    acc[config.key] = selectedValue;
                }
                return acc;
            }

            if (selectedValue !== undefined && selectedValue !== null && selectedValue !== '') {
                acc[config.key] = selectedValue;
            }
            return acc;
        }, {} as Record<string, DropdownValue>);
    }, [dropdownConfigs, dropdownValues]);

    const disabledDate = useCallback((current: Dayjs) => {
        if (!current) {
            return false;
        }

        if (!dateFilters) {
            return false;
        }

        if (minSelectableDate && current.isBefore(minSelectableDate, 'day')) {
            return true;
        }
        if (maxSelectableDate && current.isAfter(maxSelectableDate, 'day')) {
            return true;
        }

        if (typeof dateFilters.range === 'number' && dateFilters.range > 0) {
            const [rangeStart, rangeEnd] = calendarRange;
            const anchor = rangeStart ?? rangeEnd;
            if (anchor) {
                if (Math.abs(anchor.diff(current, 'day')) > dateFilters.range) {
                    return true;
                }
            }
        }

        return false;
    }, [calendarRange, dateFilters, minSelectableDate, maxSelectableDate]);
    function getFilters() {
        setFilterLoader(true);
        getReportFilterList((res) => {
            setFilterLoader(false);
            if (res?.hasError) {
                const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Reports are not present!!!'
                openNotificationWithIcon("error", errorMsg, "");
            } else {
                setReportTypes(res)
            }
        })
    }

    useEffect(() => {
        filtersRef.current = {
            selectedReportType,
            startDate,
            endDate,
        };
    }, [selectedReportType, startDate, endDate]);

    const search = useCallback((pageNumber?: number) => {
        const { selectedReportType: currentReportId, startDate: currentStart, endDate: currentEnd } = filtersRef.current;
        const currentPage = pageNumber ?? tablePagination.currentPage;
        const dynamicFilters = buildDynamicFilterPayload();

        setTablePagination((prev) => ({
            ...prev,
            loading: true,
            currentPage,
        }));

        getReportData(
            {
                reportId: currentReportId,
                startDate: currentStart ? currentStart.startOf('day').format("YYYY-MM-DD") : null,
                endDate: currentEnd ? currentEnd.startOf('day').format("YYYY-MM-DD") : null,
                pageNumber: currentPage,
                pageSize: tablePagination.pageSize,
                dynamicFilters: dynamicFilters
            },
            (res) => {
                setShowTable(true);
                if (res?.hasError) {
                    const errorMsg = res?.error?.messages?.length > 0
                        ? res.error?.messages[0].description
                        : "Unable to fetch report data.";
                    openNotificationWithIcon("error", errorMsg, "");
                    setReportData([]);
                    setTablePagination((prev) => ({
                        ...prev,
                        totalRecords: 0,
                        loading: false
                    }));
                    setTableKey((prev) => prev + 1);
                    return;
                }

                const normalizedItems = Array.isArray(res?.items)
                    ? res.items
                    : Array.isArray(res?.Items)
                        ? res.Items
                        : [];

                const rawTotalRecords = res?.totalRecords ?? res?.TotalRecords ?? normalizedItems.length;
                const parsedTotalRecords = Number(rawTotalRecords);
                const totalRecords = Number.isFinite(parsedTotalRecords) && parsedTotalRecords > 0
                    ? parsedTotalRecords
                    : normalizedItems.length;

                if (normalizedItems.length === 0 && totalRecords === 0) {
                    openNotificationWithIcon("info", "No data to display.", "");
                }

                setReportData(normalizedItems);
                setTablePagination((prev) => ({
                    ...prev,
                    totalRecords,
                    currentPage,
                    loading: false
                }));
                setTableKey((prev) => prev + 1);
            }
        );
    }, [buildDynamicFilterPayload, tablePagination.currentPage, tablePagination.pageSize]);

    function resetFilters() {
        setSelectedReportType(null);
        setReportConfig('');
        setColumns([]);
        setReportData([]);
        setLoading(false);
        setStartDate(null);
        setEndDate(null);
        setCalendarRange([null, null]);
        setShowTable(false);
        setFilterConfig(undefined);
        setDropdownOptions({});
        setDropdownValues({});
        setDropdownLoading({});
        setPrimaryKey(null);
        setTablePagination({
            ...tablePagination,
            currentPage: 1,
            totalRecords: 0,
            loading: false
        });
        setTableKey((prev) => prev + 1);
    }

    const tableChanges: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
        if (extra.action == 'sort') {
            return;
        }
        search(pagination.current);
    }
    const updateTableHeight = useCallback(() => {
        let animationFrameId: number | null = null;
        let resizeObserver: ResizeObserver | null = null;

        const measureHeight = () => {
            const container = document.getElementById(REPORT_TABLE_CONTAINER_ID);
            const headerElement = container?.getElementsByClassName("ant-table-header")[0] as HTMLElement | undefined;

            if (!container || !headerElement) {
                animationFrameId = window.requestAnimationFrame(measureHeight);
                return;
            }

            const containerHeight = container.clientHeight ?? 0;
            const headerHeight = headerElement.clientHeight ?? 0;

            if (containerHeight === 0 || headerHeight === 0) {
                animationFrameId = window.requestAnimationFrame(measureHeight);
                return;
            }

            setTableHeight(Math.max(containerHeight - headerHeight - 40, 0));
        };

        const attachResizeObserver = () => {
            const container = document.getElementById(REPORT_TABLE_CONTAINER_ID);
            const headerElement = container?.getElementsByClassName("ant-table-header")[0] as HTMLElement | undefined;

            if (!container || !headerElement) {
                animationFrameId = window.requestAnimationFrame(attachResizeObserver);
                return;
            }

            if (typeof ResizeObserver !== "undefined") {
                resizeObserver = new ResizeObserver(() => measureHeight());
                resizeObserver.observe(container);
                resizeObserver.observe(headerElement);
            }

            measureHeight();
        };

        const handleResize = () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            measureHeight();
        };

        window.addEventListener("resize", handleResize);
        attachResizeObserver();

        return () => {
            window.removeEventListener("resize", handleResize);
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
            resizeObserver?.disconnect();
        };
    }, []);

    function exportReport() {
        setExportLoader(true);
        const dynamicFilters = buildDynamicFilterPayload();
        exportReportData({
            reportId: selectedReportType,
            startDate: startDate ? startDate.startOf('day').format("YYYY-MM-DD") : null,
            endDate: endDate ? endDate.startOf('day').format("YYYY-MM-DD") : null,
            dynamicFilters: dynamicFilters
        }, (res) => {
            setExportLoader(false);
            openNotificationWithIcon("success", "You will receive the report at your registered email address.", "");
        })
    }

    function getSelectedReportData(id) {
        setReportData([]);
        setShowTable(false);
        setColumns([]);
        setTableKey((prev) => prev + 1);
        setTablePagination({
            ...tablePagination,
            currentPage: 1
        });
        setSelectedReportType(id);
        setStartDate(null);
        setEndDate(null);
        setCalendarRange([null, null]);
        setFilterConfig(undefined);
        setDropdownOptions({});
        setDropdownValues({});
        setDropdownLoading({});
        setPrimaryKey(null);
        const rep = reportTypes.find((x) => x.reportId == id);
        if (rep) {
            const tableConfig = rep?.reportConfig;
            setReportConfig(tableConfig);
            try {
                const parsedConfig = JSON.parse(tableConfig);
                setFilterConfig(parsedConfig?.Filters);
                setPrimaryKey(parsedConfig?.PrimaryKey ?? null);
            } catch (error) {
                console.error('Unable to parse report configuration', error);
                setFilterConfig(undefined);
            }
        }
    }

    const handleTableChange = useCallback((value, record, field) => {
        if (!primaryKey) {
            return;
        }
        setReportData((prevData) => {
            const index = prevData.findIndex(item => item[primaryKey] === record[primaryKey]);
            if (index === -1) {
                return prevData;
            }
            const updated = [...prevData];
            updated[index] = {
                ...updated[index],
                [field]: value ? value.format('YYYY-MM-DDTHH:mm:ss') : ''
            };
            return updated;
        });
    }, [primaryKey]);

    const getTableRowKey = useCallback((record: any, index?: number) => {
        const keyValue = primaryKey ? record?.[primaryKey] : undefined;
        if (keyValue !== undefined && keyValue !== null && keyValue !== '') {
            return `${keyValue}-${tablePagination.currentPage}-${index ?? 0}`;
        }
        return `row-${tablePagination.currentPage}-${index ?? 0}`;
    }, [primaryKey, tablePagination.currentPage]);
    //Use Effects
    useEffect(() => {
        getFilters();
        document.title = "SSRS Reports";
    }, [])

    useEffect(() => {
        if (!columns?.length || !showTable || !reportData?.length) {
            return;
        }

        const cleanup = updateTableHeight();
        return cleanup;
    }, [columns, reportData?.length, showTable, updateTableHeight]);
    useEffect(() => {
        if (!showTable || !reportData?.length) {
            return;
        }

        resetTableScrollPosition();
    }, [reportData, resetTableScrollPosition, showTable, tableKey]);

    useEffect(() => {
        if (reportData?.length && !columns?.length && reportConfig) {
            const cols = createTableConfig(
                JSON.parse(reportConfig).Columns,
                handleTableChange,
                { onComponentAction: handleDynamicComponentAction }
            ) || [];
            setColumns(cols);
        }
    }, [reportData, columns, reportConfig, handleTableChange, handleDynamicComponentAction]);

  return (
      <ConfigProvider theme={{
          components: {
              Layout: { bodyBg: "#eff1f5", siderBg: "#fff" }
          }
      }} >
          {contextHolder}
          <Layout style={{
              padding: '20px',
              marginBottom: '5px',
              marginTop: '5px',
              width: '100%'
          }}>
              <h3 style={{ textTransform: 'uppercase', marginTop: '0px' }}>Reports</h3>
              <Flex justify='space-between' style={{ padding: "1rem", alignItems: "center", background: '#fff' }}>
                  <label><SearchOutlined />&nbsp;Search</label>  
              </Flex>
              <Skeleton loading={filterLoader} active>
                  <Row type="flex" style={{ padding: '1rem', paddingTop: "0px", background: '#fff' }}>
                      <Col span={6}>
                          <Select
                              style={{ width: '90%' }}
                              placeholder="Select Report Type"
                              onChange={(e) => getSelectedReportData(e)}
                              value={selectedReportType}
                              allowClear={false}
                          >
                              {reportTypes && reportTypes.map((item) => (
                                  <Option key={item.reportId}>{item.reportName}</Option>
                              ))}
                          </Select>
                      </Col>
                      {
                          isDateFilterEnabled &&
                          <Col span={6}>
                              <RangePicker
                                  placeholder={["From Date", "To Date"]}
                                  style={{ width: '100%' }}
                                  format="MM/DD/YYYY"
                                  minDate={minSelectableDate}
                                  maxDate={maxSelectableDate}
                                  value={calendarRange}
                                  disabledDate={disabledDate}
                                  onCalendarChange={(dates) => {
                                      if (Array.isArray(dates)) {
                                          const [rangeStart, rangeEnd] = normalizeRange(dates[0] ?? null, dates[1] ?? null);
                                          setCalendarRange([rangeStart, rangeEnd]);
                                          setStartDate(rangeStart);
                                          setEndDate(rangeEnd);
                                      } else {
                                          setCalendarRange([null, null]);
                                          setStartDate(null);
                                          setEndDate(null);
                                      }
                                  }}
                                  onChange={(dates) => {
                                      if (Array.isArray(dates) && dates.length) {
                                          const [rangeStart, rangeEnd] = normalizeRange(dates[0] ?? null, dates[1] ?? null);
                                          setStartDate(rangeStart);
                                          setEndDate(rangeEnd);
                                          setCalendarRange([rangeStart, rangeEnd]);
                                      } else {
                                          setStartDate(null);
                                          setEndDate(null);
                                          setCalendarRange([null, null]);
                                      }
                                  }}
                              />
                          </Col>
                      }
                  </Row>
                  {dropdownConfigs.length > 0 && (
                      <Row
                          gutter={[16, 16]}
                          style={{ padding: '0 1rem 1rem', paddingTop: 0, background: '#fff' }}
                      >
                          {dropdownConfigs.map((config) => {
                              const isMultiple = config.mode === 'multiple';
                              const currentValue = dropdownValues?.[config.key];
                              const selectValue = isMultiple
                                  ? (Array.isArray(currentValue) ? currentValue : [])
                                  : (currentValue === null || currentValue === undefined ? undefined : currentValue);
                              return (
                                  <Col span={6} key={config.key}>
                                      <label style={{ display: 'block', marginBottom: '4px' }}>{config.label}</label>
                                      <Select
                                          mode={isMultiple ? 'multiple' : undefined}
                                          allowClear
                                          placeholder={`Select ${config.label}`}
                                          style={{ width: '100%' }}
                                          options={dropdownOptions[config.key] ?? []}
                                          value={selectValue as string | number | (string | number)[] | undefined}
                                          loading={!!dropdownLoading[config.key]}
                                          onChange={(value) => {
                                              if (isMultiple) {
                                                  handleDropdownValueChange(config.key, Array.isArray(value) ? value : []);
                                                  return;
                                              }
                                              handleDropdownValueChange(config.key, (value ?? null) as DropdownValue);
                                          }}
                                          onClear={() => handleDropdownValueChange(config.key, isMultiple ? [] : null)}
                                          maxTagCount={isMultiple ? 'responsive' : undefined}
                                      />
                                  </Col>
                              );
                          })}
                      </Row>
                  )}
                  <Flex justify='end' style={{ marginBottom: "2rem", padding: "1rem", paddingTop: "0px", background: '#fff' }} gap='small'>
                      <Button onClick={() => search()} loading={tablePagination.loading} style={{ marginLeft: '5px' }} disabled={!(selectedReportType && (!isDateFilterEnabled || (isDateFilterEnabled && startDate && endDate)))} type="primary">
                          Search
                      </Button>
                      <Button onClick={resetFilters} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', marginLeft: '5px', color: 'rgba(0, 0, 0, 0.88)' }} type="primary">
                          Reset
                      </Button>
                  </Flex>
              </Skeleton>
              {showTable &&
                <Card styles={{ body: { padding: '15px', paddingTop: '10px', height: "auto", maxHeight: reportData?.length > 0 ? "calc(100vh - 290px)" : undefined } }}>
                      {
                          reportData?.length > 0 &&
                          <Flex justify='end' style={{ paddingBottom: "5px", paddingTop: "0", alignItems: "center", background: '#fff' }} gap='small'>
                                  <Button key="export" loading={exportLoader} onClick={exportReport} style={{ background: '#1677ff', color: '#fff' }}>
                                      Export
                                  </Button>
                          </Flex>
                      }
                      {
                          reportData?.length > 0 &&
                          <div id={REPORT_TABLE_CONTAINER_ID} style={{ height: "calc(100vh - 388px)", width: "100%", overflow: "hidden" }}>
                              <Table
                                  scroll={{
                                      y: tableHeight,
                                      x: columns.reduce((acc: number, col: any) => acc + (col.width || 150), 0),
                                  }}
                                  key={tableKey}
                                  dataSource={reportData}
                                  pagination={{
                                      defaultCurrent: 1,
                                      current: tablePagination.currentPage,
                                      pageSize: tablePagination.pageSize,
                                      total: tablePagination.totalRecords,
                                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                      showSizeChanger: false,
                                  }}
                                      columns={columns}
                                  onChange={tableChanges}
                                  rowKey={getTableRowKey}
                                  size="small"
                                  loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: tablePagination.loading }}
                                  onHeaderRow={(columns, index) => {
                                      return {
                                          onContextMenu: (event) => {
                                              event.preventDefault();
                                              if (!menuVisible) {
                                                  document.addEventListener(`click`, function onClickOutside() {
                                                      setMenuVisible(false);
                                                      document.removeEventListener(`click`, onClickOutside);
                                                  });
                                              }
                                              setMenuVisible(true);
                                          }
                                      };
                                  }}
                              />
                          </div>
                      }
                  </Card>
              }
          </Layout>
          <Modal
              open={busPassModalOpen}
              onCancel={handleBusPassModalClose}
              footer={null}
              title="Transit Pass Request"
              destroyOnClose
              maskClosable={false}
              width={720}
          >
              <BusPassReportForm
                  initialValues={busPassFormValues}
                  onReportGenerated={handleBusPassReportGenerated}
              />
          </Modal>
          <Modal
              open={updateApptModalOpen}
              onCancel={handleUpdateApptModalClose}
              footer={null}
              title="Update Appointment Time"
              destroyOnClose
              maskClosable={false}
              width={600}
          >
              <UpdateAppointmentTime
                  initialValues={updateApptValues}
                  onSuccess={handleAppointmentUpdated}
              />
          </Modal>
      </ConfigProvider>
  );
};

export default Reports;
